// provider-registry.js — keeps one CalendarProvider instance per accountId.
//
// The settings store carries the canonical list of accounts in
// `calendars.accounts`. The registry watches that list and hydrates /
// disposes provider instances to match: adding an account creates a
// provider, removing one closes it. Two Google accounts are two separate
// instances of GoogleProvider keyed by different accountIds.
//
// Public surface
//   ProviderRegistry.init()                — wire the Settings subscription
//   ProviderRegistry.get(accountId)        — instance or null
//   ProviderRegistry.list()                — [{accountId, provider, instance}]
//   ProviderRegistry.register(kind, ctor)  — make a provider kind available
//   ProviderRegistry.add(spec)             — append spec to settings + hydrate
//   ProviderRegistry.remove(accountId)     — drop spec from settings + close
//   ProviderRegistry.reload()              — re-read settings + rebuild map
//
// The orchestrator (calendar-sync.js) does not touch this module directly —
// it iterates `ProviderRegistry.list()` once per sync run.

import { Settings } from '../settings/settings-store.js';

// Registered constructors keyed by provider kind ('google', 'caldav', …).
// Populated by provider files at load time via an explicit ESM import of
// `ProviderRegistry.register(...)` — see each providers/*-provider.js.
const _ctors = new Map();

// Live instances keyed by accountId. Created lazily on first reload().
const _instances = new Map();

let _wired = false;
let _unsubscribe: any = null;
// Serializes reload() so two overlapping calls can't interleave across the
// _hydrate() await (see reload() below).
let _reloadInflight: Promise<void> | null = null;

/**
 * @param {string} kind - provider kind ('google', 'caldav', …)
 * @param {any} ctor - the provider constructor
 * @returns {void}
 */
function register(kind: string, ctor: any) {
  if (!kind || typeof ctor !== 'function') {
    throw new TypeError('register(kind, ctor): both required');
  }
  _ctors.set(kind, ctor);
}

/**
 * @param {string} accountId
 * @returns {any} the provider instance, or null.
 */
function get(accountId: string) {
  return _instances.get(accountId) || null;
}
/** @returns {any[]} [{ accountId, provider, instance }] */
function list() {
  const out = [];
  for (const [accountId, instance] of _instances) {
    out.push({ accountId, provider: instance.id, instance });
  }
  return out;
}

// ── Hydration ──
/**
 * @param {any} spec - an account spec
 * @returns {Promise<any>} the hydrated instance, or null.
 */
async function _hydrate(spec: any) {
  const Ctor = _ctors.get(spec.provider);
  if (!Ctor) {
    console.warn('[provider-registry] no constructor for', spec.provider);
    return null;
  }
  try {
    const inst = new Ctor({
      id: spec.provider,
      accountId: spec.accountId,
      credentialKey: spec.credentialKey,
      label: spec.label,
      serverUrl: spec.serverUrl,
    });
    await inst.init();
    return inst;
  } catch (e: any) {
    console.warn('[provider-registry] init failed for', spec.accountId, e);
    return null;
  }
}

// Serialized entry point. The settings subscription (on a `calendars.accounts`
// change) and the periodic sync (calendar-sync calls reload() at the top of a
// run) can both fire reload() at overlapping times. Because _reloadOnce awaits
// network _hydrate()s mid-loop, two concurrent runs would interleave: both see
// `_instances.has(id)` still false and double-hydrate (the first instance leaks
// un-closed), or one's removal pass closes an instance the other still treats
// as live. Chain each call after the in-flight one so the map mutates serially;
// each run re-reads the latest settings when it starts.
/** @returns {Promise<void>} */
function reload(): Promise<void> {
  const run = Promise.resolve(_reloadInflight)
    .catch(() => {})
    .then(() => _reloadOnce());
  const tracked: Promise<void> = run
    .catch(() => {})
    .finally(() => {
      if (_reloadInflight === tracked) _reloadInflight = null;
    });
  _reloadInflight = tracked;
  return run;
}

/** @returns {Promise<void>} */
async function _reloadOnce() {
  const accounts = Settings?.get('calendars.accounts', []) || [];
  const seen = new Set();

  // Add/update.
  for (const spec of accounts) {
    if (!spec?.accountId) continue;
    seen.add(spec.accountId);
    if (_instances.has(spec.accountId)) continue;
    const inst = await _hydrate(spec);
    if (inst) _instances.set(spec.accountId, inst);
  }

  // Remove instances whose account specs are gone.
  for (const accountId of Array.from(_instances.keys())) {
    if (seen.has(accountId)) continue;
    const inst = _instances.get(accountId);
    _instances.delete(accountId);
    try {
      await inst.close();
    } catch {}
  }
}

/** @returns {void} */
function init() {
  if (_wired) return;
  _wired = true;
  // Re-hydrate whenever the accounts list mutates.
  if (Settings && typeof Settings.subscribe === 'function') {
    _unsubscribe = Settings.subscribe('calendars.accounts', () => {
      reload().catch((e) => console.warn('[provider-registry] reload failed:', e));
    });
  }
  // First hydration. Don't block init on Settings.load — the subscription
  // will fire as soon as it lands.
  Promise.resolve()
    .then(() => Settings?.load?.())
    .then(() => reload())
    .catch((e) => console.warn('[provider-registry] initial hydrate failed:', e));
}

// ── Mutators ──
// These keep settings + instance map in sync. Callers (settings panel,
// OAuth callback) should prefer these to mutating settings directly so
// the registry never lags behind the persisted state.
/**
 * @param {any} spec - { accountId, provider, label?, credentialKey?, serverUrl? }
 * @returns {Promise<void>}
 */
async function add(spec: any) {
  if (!spec?.accountId || !spec.provider) {
    throw new TypeError('add(spec): accountId + provider required');
  }
  const accounts = (Settings?.get('calendars.accounts', []) || []).slice();
  if (accounts.some((a: any) => a.accountId === spec.accountId)) {
    throw new Error(`account already registered: ${spec.accountId}`);
  }
  accounts.push({
    accountId: spec.accountId,
    provider: spec.provider,
    label: spec.label || spec.accountId,
    credentialKey: spec.credentialKey || null,
    serverUrl: spec.serverUrl || null,
    addedAt: new Date().toISOString(),
  });
  await Settings.set('calendars.accounts', accounts);
  // subscribe() above will fire and reload(). Awaiting here would race
  // with the subscription; let the subscriber catch up.
}

/**
 * @param {string} accountId
 * @returns {Promise<void>}
 */
async function remove(accountId: string) {
  if (!accountId) return;
  const accounts = (Settings?.get('calendars.accounts', []) || []).filter(
    (a: any) => a.accountId !== accountId
  );
  // Also prune the selected-calendars list so we don't leave dangling
  // (accountId, calendarId) tuples pointing at a removed account.
  const selected = (Settings?.get('calendars.selected', []) || []).filter(
    (s: any) => s.accountId !== accountId
  );
  await Settings.set('calendars.accounts', accounts);
  await Settings.set('calendars.selected', selected);
}

// Test-only reset. Wiped between tests so module-level maps don't leak.
/** @returns {void} */
function _resetForTests() {
  _instances.clear();
  _ctors.clear();
  _reloadInflight = null;
  if (_unsubscribe) {
    try {
      _unsubscribe();
    } catch {}
    _unsubscribe = null;
  }
  _wired = false;
}

// Phase 4.7: real ESM. Providers import this and self-register; the settings
// panel + oauth-web import it directly. It is also mirrored onto
// `window.ProviderRegistry` as a runtime seam for calendar-sync.js, which
// reaches the registry off window (it lives outside this partition).
export const ProviderRegistry = {
  init,
  get,
  list,
  register,
  add,
  remove,
  reload,
  _resetForTests,
};

if (typeof window !== 'undefined') window.ProviderRegistry = ProviderRegistry;
